import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import http from '@lib/http-client'
import { toUtcIsoString } from '@utils/date'
import {
  FollowUpDashboardParams,
  FollowUpDashboardResponse,
  FollowUpStatus,
  FollowUpsStore,
  IFollowUp,
  IFollowUpDashboardItem
} from './types'

const normalizeFollowUp = (followUp?: IFollowUp | null): IFollowUp | null =>
  followUp
    ? {
        ...followUp,
        scheduled_date:
          toUtcIsoString(followUp.scheduled_date) ?? followUp.scheduled_date
      }
    : null

const normalizeDashboardItem = (
  item: IFollowUpDashboardItem
): IFollowUpDashboardItem => ({
  ...item,
  scheduled_date: toUtcIsoString(item.scheduled_date) ?? item.scheduled_date,
  last_visit_date: item.last_visit_date
    ? toUtcIsoString(item.last_visit_date) ?? item.last_visit_date
    : null
})

export const useFollowUpsStore = create<FollowUpsStore>()(
  devtools(() => ({
    getFollowUpDashboard: async ({
      page = 0,
      page_size = 50,
      professional_ids = [],
      company_ids = [],
      order_by = 'SCHEDULED_DATE_ASC'
    }: FollowUpDashboardParams = {}) => {
      try {
        const params = new URLSearchParams()
        params.append('page', String(page))
        params.append('page_size', String(page_size))
        params.append('order_by', order_by)
        // NOTE: the endpoint expects these repeated, not comma separated
        professional_ids.forEach(id =>
          params.append('professional_ids', String(id))
        )
        company_ids.forEach(id => params.append('company_ids', String(id)))

        const response = await http.get(
          `/dashboard/followup?${params.toString()}`
        )

        if (!response || !Array.isArray(response.results)) {
          return null
        }

        return {
          ...response,
          results: response.results.map(normalizeDashboardItem)
        } as FollowUpDashboardResponse
      } catch (error) {
        console.error('Error fetching follow up dashboard:', error)
        return null
      }
    },

    getMemberScheduledFollowUp: async (memberId: number) => {
      try {
        const response = await http.get(`/user/${memberId}/followup/scheduled`)

        // the endpoint returns every scheduled follow up, ordered by date
        return normalizeFollowUp(Array.isArray(response) ? response[0] : null)
      } catch (error) {
        console.error('Error fetching member scheduled follow up:', error)
        return null
      }
    },

    scheduleMemberFollowUp: async (memberId: number, scheduleDate: string) => {
      try {
        const response = await http.post(`/user/${memberId}/followup`, {
          schedule_date: scheduleDate
        })

        return normalizeFollowUp(response)
      } catch (error) {
        console.error('Error scheduling member follow up:', error)
        return null
      }
    },

    updateFollowUpStatus: async (
      followUpId: number,
      status: FollowUpStatus
    ) => {
      try {
        const response = await http.post(`/followup/${followUpId}/status`, {
          status
        })

        return normalizeFollowUp(response)
      } catch (error) {
        console.error('Error updating follow up status:', error)
        return null
      }
    }
  }))
)
